import React, { useEffect, useState } from "react";
import { Loader2, Mail, RotateCcw, Send, Save, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState({ subject: "", html_body: "" });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [previewEmail, setPreviewEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates", { withCredentials: true });
      setTemplates(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const selected = templates.find((t) => t.key === selectedKey) || null;

  useEffect(() => {
    if (selected) {
      setDraft({ subject: selected.subject, html_body: selected.html_body });
    }
  }, [selectedKey, selected]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${selected.key}`, draft, { withCredentials: true });
      toast.success("Template saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selected) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${selected.key}/reset`, {}, { withCredentials: true });
      setDraft({ subject: data.subject, html_body: data.html_body });
      toast.success("Template reset to default");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const sendPreview = async () => {
    if (!selected || !previewEmail) {
      toast.error("Enter an email to send the preview to");
      return;
    }
    setSending(true);
    try {
      // Save first so preview reflects current edits
      await api.put(`/admin/email-templates/${selected.key}`, draft, { withCredentials: true });
      const { data } = await api.post(
        `/admin/email-templates/${selected.key}/preview`,
        { to_email: previewEmail },
        { withCredentials: true }
      );
      if (data?.ok) {
        toast.success(`Preview sent to ${previewEmail}`);
      } else {
        toast.error(data?.error || "Send failed");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="admin-email-templates-loading">
        <Loader2 className="w-6 h-6 animate-spin text-spike-blue" />
      </div>
    );
  }

  // Detail view
  if (selected) {
    return (
      <div className="space-y-4" data-testid="admin-email-templates-detail">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSelectedKey(null)}
            data-testid="email-template-back-btn"
            className="inline-flex items-center gap-1 text-sm text-spike-blue-light hover:text-spike-blue"
          >
            <ChevronLeft className="w-4 h-4" />
            All templates
          </button>
        </div>

        <div className="bg-spike-card border border-white/10 rounded-lg p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-orbitron font-bold text-spike-text uppercase tracking-wide">
                {selected.name}
              </h2>
              <p className="text-sm text-spike-text-muted mt-1 font-exo">{selected.description}</p>
              <div className="text-xs text-spike-dim mt-2 font-exo">
                key <code className="text-spike-blue-light">{selected.key}</code>
                {selected.is_overridden ? <span className="ml-3 text-amber-400">(admin-edited)</span> : <span className="ml-3 text-spike-dim">(default)</span>}
                {selected.updated_at ? <span className="ml-3">Last updated {new Date(selected.updated_at).toLocaleString()}</span> : null}
              </div>
            </div>
            <button
              type="button"
              onClick={reset}
              disabled={resetting || !selected.is_overridden}
              data-testid="email-template-reset-btn"
              className="inline-flex items-center gap-2 text-xs font-orbitron uppercase tracking-[0.15em] text-spike-text-muted hover:text-spike-text border border-white/15 hover:border-white/30 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2 rounded-sm"
            >
              {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Reset to default
            </button>
          </div>
        </div>

        <div className="bg-spike-card border border-white/10 rounded-lg p-5 space-y-4">
          <div>
            <label className="block text-xs font-orbitron uppercase tracking-[0.18em] text-spike-text-muted mb-2">
              Subject line
            </label>
            <input
              type="text"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              data-testid="email-template-subject-input"
              className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue font-exo"
              placeholder="e.g., Welcome to Spike Sports"
            />
          </div>

          <div>
            <label className="block text-xs font-orbitron uppercase tracking-[0.18em] text-spike-text-muted mb-2">
              Body HTML
              <span className="ml-2 text-spike-dim normal-case tracking-normal font-exo">
                — the wrapper (logo, footer) is added automatically
              </span>
            </label>
            <textarea
              value={draft.html_body}
              onChange={(e) => setDraft({ ...draft, html_body: e.target.value })}
              data-testid="email-template-body-textarea"
              rows={14}
              className="w-full bg-black/60 border border-white/15 rounded-md px-3 py-2.5 text-xs text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue font-mono leading-relaxed"
              spellCheck={false}
            />
          </div>

          <div>
            <label className="block text-xs font-orbitron uppercase tracking-[0.18em] text-spike-text-muted mb-2">
              Available variables
            </label>
            <div className="flex flex-wrap gap-2">
              {selected.available_vars.map((v) => (
                <span
                  key={v.name}
                  title={v.description}
                  className="text-xs font-mono bg-black/60 border border-spike-blue/30 text-spike-blue-light px-2.5 py-1 rounded-sm"
                  data-testid={`email-template-var-${v.name}`}
                >
                  {`{{${v.name}}}`}
                </span>
              ))}
            </div>
            <p className="text-xs text-spike-dim mt-2 font-exo">
              Use double curly braces — e.g., {`{{first_name}}`}. Unknown vars become empty strings.
            </p>
          </div>
        </div>

        <div className="bg-spike-card border border-white/10 rounded-lg p-5 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="email-template-save-btn"
              className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-black font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <input
                type="email"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="email-template-preview-email-input"
                className="bg-black/60 border border-white/15 rounded-md px-3 py-2.5 text-sm text-spike-text placeholder:text-spike-dim focus:outline-none focus:border-spike-blue font-exo w-56"
              />
              <button
                type="button"
                onClick={sendPreview}
                disabled={sending || !previewEmail}
                data-testid="email-template-send-preview-btn"
                className="inline-flex items-center gap-2 border border-spike-blue/50 hover:border-spike-blue text-spike-blue-light hover:text-spike-blue disabled:opacity-50 font-orbitron text-xs font-bold uppercase tracking-[0.2em] px-4 py-2.5 rounded-sm transition-colors"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send test
              </button>
            </div>
          </div>
          <p className="text-xs text-spike-dim font-exo">
            &quot;Send test&quot; auto-saves your changes first, then sends a preview with sample variable values.
          </p>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4" data-testid="admin-email-templates-page">
      <div>
        <h2 className="text-xl font-orbitron font-bold text-spike-text uppercase tracking-wide">
          Email templates
        </h2>
        <p className="text-sm text-spike-text-muted mt-1 font-exo">
          Edit subject and body for every transactional email. The brand header (logo) and footer are added automatically.
        </p>
      </div>

      <div className="grid gap-3">
        {templates.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setSelectedKey(t.key)}
            data-testid={`email-template-row-${t.key}`}
            className="text-left bg-spike-card hover:bg-spike-card/80 border border-white/10 hover:border-spike-blue/30 rounded-lg p-4 transition-colors group"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 w-9 h-9 flex items-center justify-center rounded-md bg-black/40 border border-spike-blue/30 text-spike-blue-light flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-orbitron font-bold text-spike-text uppercase tracking-wide">
                    {t.name}
                  </h3>
                  {t.is_overridden ? (
                    <span className="text-[10px] font-orbitron uppercase tracking-[0.15em] text-amber-400 border border-amber-400/30 px-2 py-0.5 rounded-sm">
                      edited
                    </span>
                  ) : (
                    <span className="text-[10px] font-orbitron uppercase tracking-[0.15em] text-spike-dim border border-white/10 px-2 py-0.5 rounded-sm">
                      default
                    </span>
                  )}
                </div>
                <p className="text-sm text-spike-text-muted mt-1 font-exo">{t.description}</p>
                <p className="text-xs text-spike-dim mt-2 font-exo truncate">
                  Subject: <span className="text-spike-text-muted">{t.subject}</span>
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
